"use client"
import { useEffect, useState, useCallback } from "react"
import { useTranslations } from "next-intl"
import { Apple, Play, X } from "lucide-react"
import Image from "next/image"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/shared/ui/dialog"

type Props = {
  appleUrl?: string
  googleUrl?: string
  delayMs?: number
  // When true (default), the modal appears on every page visit after delay.
  // When false, it appears once per browser session.
  showEveryVisit?: boolean
  // Global on/off flag
  enabled?: boolean
}

export default function AppPromoModal({ appleUrl = "#", googleUrl = "#", delayMs = 1200, showEveryVisit = true, enabled = true }: Props) {
  if (!enabled) return null
  const t = useTranslations("Home.app-promo")
  const [open, setOpen] = useState(false)

  // Session key when gating is enabled
  const storageKey = "appPromoSeenSession"

  const markSeen = useCallback(() => {
    try {
      sessionStorage.setItem(storageKey, "1")
    } catch { }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (showEveryVisit) {
        setOpen(true)
        return
      }
      try {
        const seen = sessionStorage.getItem(storageKey)
        if (!seen) setOpen(true)
      } catch {
        setOpen(true)
      }
    }, delayMs)
    return () => clearTimeout(timer)
  }, [delayMs, showEveryVisit])

  const onClose = (val: boolean) => {
    setOpen(val)
  if (!val && !showEveryVisit) markSeen()
  }

  const onAction = (href: string) => {
  if (!showEveryVisit) markSeen()
    window.open(href, "_blank", "noopener,noreferrer")
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="w-full max-w-2xl border-none bg-gradient-to-br from-sub_primary via-white to-sub_primary shadow-2xl sm:rounded-3xl p-0 overflow-hidden"
      >
        <button
          aria-label="close"
          onClick={() => onClose(false)}
          className="absolute left-4 top-4 z-10 rounded-full bg-black/5 p-2 text-title_color hover:bg-black/10"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="grid grid-cols-1 items-stretch gap-0 md:grid-cols-[1fr,1.2fr]">
          {/* Visual */}
          <div className="relative order-1 md:order-2 p-6 md:p-8">
            <div className="mx-auto relative aspect-[9/16] w-40 md:w-48 lg:w-56">
              <div className="absolute inset-0 rounded-[1.75rem] bg-gradient-to-br from-primary/15 to-secondary/20 p-1 shadow-[0_10px_60px_-15px_rgba(0,91,150,0.35)]">
                <div className="relative h-full w-full overflow-hidden rounded-[1.5rem] bg-white">
                    <Image src="/App.png" alt="Dr & Coach App" fill sizes="(max-width: 768px) 14rem, 18rem" className="object-contain" />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-white/10" />
                </div>
              </div>
            </div>
          </div>

          {/* Text + CTAs */}
          <div className="order-2 md:order-1 p-6 md:p-8">
            <DialogHeader className="text-right">
              <DialogTitle className="text-secondary text-xl md:text-2xl mb-1">{t("title")}</DialogTitle>
              <DialogDescription className="text-primary text-2xl md:text-3xl font-bold !mt-0">
                {t("subTitle")}
              </DialogDescription>
            </DialogHeader>
            <p className="text-sub_text_color mt-2 mb-4 text-sm md:text-base">{t("desc")}</p>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => onAction(appleUrl)}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-primary to-secondary px-4 py-3 text-white shadow-lg transition hover:scale-[1.02]"
              >
                <span className="rounded-xl bg-white/15 p-2 backdrop-blur"><Apple className="h-5 w-5" /></span>
                <span className="text-right">
                  <span className="block text-[10px] opacity-90">{t("availableOn")}</span>
                  <span className="block text-sm font-bold">App Store</span>
                </span>
              </button>
              <button
                onClick={() => onAction(googleUrl)}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-secondary to-primary px-4 py-3 text-white shadow-lg transition hover:scale-[1.02]"
              >
                <span className="rounded-xl bg-white/15 p-2 backdrop-blur"><Play className="h-5 w-5" /></span>
                <span className="text-right">
                  <span className="block text-[10px] opacity-90">{t("getItOn")}</span>
                  <span className="block text-sm font-bold">Google Play</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
